import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { TransactionStageType } from '@client/graphql/__generated__/types';
import { Select } from '@document/common/Select';
import { PIPELINE_COLUMNS } from '../Pipeline/constants';

interface EventTypeSelectProps {
  value: TransactionStageType;
  onChange: (value: TransactionStageType) => void;
  disabled?: boolean;
}

interface EventTypeOption {
  value: TransactionStageType;
  label: string;
  icon: React.ElementType;
}

const options: EventTypeOption[] = PIPELINE_COLUMNS.map((col) => ({
  value: col.eventType,
  label: col.label,
  icon: col.icon,
}));

const darkSelectStyles = {
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    bg: 'whiteAlpha.50',
    borderColor: 'whiteAlpha.100',
    minHeight: '70px',
    _hover: { borderColor: 'whiteAlpha.300' },
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    bg: state.isSelected
      ? 'whiteAlpha.200'
      : state.isFocused
        ? 'whiteAlpha.100'
        : 'transparent',
    color: 'white',
    _hover: { bg: 'whiteAlpha.100' },
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'white',
  }),
};

export const EventTypeSelect = ({
  value,
  onChange,
  disabled,
}: EventTypeSelectProps) => {
  const selectedOption = options.find((opt) => opt.value === value);

  const formatOptionLabel = (option: EventTypeOption) => (
    <Flex alignItems="center" gap={3}>
      <Flex
        alignItems="center"
        bgGradient="linear(to-br, purple.400, purple.600)"
        borderRadius="sm"
        justifyContent="center"
        minHeight="40px"
        minWidth="40px"
      >
        <Icon as={option.icon} boxSize={5} color="white" />
      </Flex>
      <Box>
        <Text
          color="whiteAlpha.600"
          fontSize="xs"
          letterSpacing="wider"
          textTransform="uppercase"
        >
          Event
        </Text>
        <Text color="white">{option.label}</Text>
      </Box>
    </Flex>
  );

  return (
    <Select<EventTypeOption>
      chakraStyles={darkSelectStyles}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={(opt) => opt.label}
      getOptionValue={(opt) => opt.value}
      isDisabled={disabled}
      isSearchable={false}
      options={options}
      value={selectedOption}
      onChange={(newValue) => {
        if (newValue) {
          onChange(newValue.value);
        }
      }}
    />
  );
};
